import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { supabase } from "../../supabaseClient"
import { Card, CardContent } from "../../components/ui/card"
import { getActiveYear } from "../../utils/activeYear"

interface Entry {
  id: string
  entry_name: string
  is_paid: boolean
  created_at: string
  user_id: string
  display_name?: string
  email?: string
}

export default function PaymentManager() {
  const [entries, setEntries] = useState<Entry[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<'all' | 'paid' | 'unpaid'>('all')
  const [updating, setUpdating] = useState<string | null>(null)
  const [activeYear, setActiveYearState] = useState<number | null>(null)
  const [searchQuery, setSearchQuery] = useState('')

  useEffect(() => {
    loadActiveYear()
  }, [])

  async function loadActiveYear() {
    const year = await getActiveYear()
    setActiveYearState(year)
    loadEntries(year)
  }

  async function loadEntries(year: number) {
    const { data: entriesData } = await supabase
      .from('entries')
      .select('id, entry_name, is_paid, created_at, user_id')
      .eq('year', year)
      .order('created_at', { ascending: false })

    if (!entriesData) {
      setLoading(false)
      return
    }

    // Get profiles for display names and emails
    const userIds = Array.from(new Set(entriesData.map(e => e.user_id)))
    const { data: profiles } = await supabase
      .from('profiles')
      .select('id, display_name, email')
      .in('id', userIds)

    const profileMap = new Map(profiles?.map(p => [p.id, p]) || [])

    const entriesWithProfiles = entriesData.map(entry => ({
      ...entry,
      display_name: profileMap.get(entry.user_id)?.display_name,
      email: profileMap.get(entry.user_id)?.email
    }))

    setEntries(entriesWithProfiles)
    setLoading(false)
  }

  async function togglePayment(entryId: string, currentStatus: boolean) {
    setUpdating(entryId)
    
    const { error } = await supabase
      .from('entries')
      .update({ is_paid: !currentStatus })
      .eq('id', entryId)

    if (!error) {
      setEntries(entries.map(e => 
        e.id === entryId ? { ...e, is_paid: !currentStatus } : e
      ))
    }
    
    setUpdating(null)
  }

  const filteredEntries = entries.filter(e => {
    // Payment status filter
    if (filter === 'paid' && !e.is_paid) return false
    if (filter === 'unpaid' && e.is_paid) return false
    
    // Search filter
    if (searchQuery) {
      const q = searchQuery.toLowerCase()
      return e.entry_name.toLowerCase().includes(q) ||
             e.display_name?.toLowerCase().includes(q) ||
             e.email?.toLowerCase().includes(q)
    }
    
    return true
  })

  const paidCount = entries.filter(e => e.is_paid).length
  const unpaidCount = entries.filter(e => !e.is_paid).length

  if (loading) {
    return <div className="flex items-center justify-center min-h-[60vh] text-gray-500">Loading entries...</div>
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/admin" className="text-gray-400 hover:text-gray-600">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-[#1e3a5f]">Payment Manager</h1>
          <p className="text-gray-500">Manage payment status for <span className="font-semibold text-[#F15A22]">{activeYear}</span></p>
        </div>
      </div>

      {/* Stats - Compact Row */}
      <div className="flex gap-4 text-sm">
        <div className="flex items-center gap-2">
          <span className="font-bold text-[#1e3a5f] text-lg">{entries.length}</span>
          <span className="text-gray-500">entries</span>
        </div>
        <div className="text-gray-300">|</div>
        <div className="flex items-center gap-2">
          <span className="font-bold text-green-600 text-lg">{paidCount}</span>
          <span className="text-gray-500">paid</span>
        </div>
        <div className="text-gray-300">|</div>
        <div className="flex items-center gap-2">
          <span className="font-bold text-red-500 text-lg">{unpaidCount}</span>
          <span className="text-gray-500">unpaid</span>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-lg p-1 w-fit">
        {(['all', 'paid', 'unpaid'] as const).map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              filter === f
                ? 'bg-white text-[#1e3a5f] shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
            {f === 'all' && ` (${entries.length})`}
            {f === 'paid' && ` (${paidCount})`}
            {f === 'unpaid' && ` (${unpaidCount})`}
          </button>
        ))}
      </div>

      {/* Quick Search */}
      <div className="relative">
        <input
          type="text"
          placeholder="Search entries, users, emails..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-9 pr-3 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20"
        />
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
          <circle cx="11" cy="11" r="8"/>
          <path d="m21 21-4.3-4.3"/>
        </svg>
        {searchQuery && (
          <button
            onClick={() => setSearchQuery('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        )}
      </div>

      {/* Search results info */}
      {searchQuery && (
        <div className="text-sm text-gray-500">
          Showing {filteredEntries.length} of {entries.length} entries
        </div>
      )}

      {/* Entries List */}
      <Card className="bg-white border-0 shadow-sm overflow-hidden">
        <CardContent className="p-0">
          {filteredEntries.length === 0 ? (
            <div className="py-12 text-center text-gray-500">
              <p className="font-medium">No entries found</p>
              <p className="text-sm mt-1">No entries match your current filter</p>
            </div>
          ) : (
            <>
              <div className="divide-y divide-gray-100">
                {filteredEntries.map(entry => (
                  <div 
                    key={entry.id} 
                    className={`p-4 ${entry.is_paid ? 'bg-green-50/50' : ''}`}
                  >
                    {/* Mobile-first stacked layout */}
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-[#1e3a5f]">{entry.entry_name}</div>
                        <div className="text-sm text-gray-600 mt-0.5">{entry.display_name || '—'}</div>
                        <div className="text-sm text-gray-400 truncate" title={entry.email}>
                          {entry.email || '—'}
                        </div>
                      </div>
                      <button
                        onClick={() => togglePayment(entry.id, entry.is_paid)}
                        disabled={updating === entry.id}
                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all shrink-0 ${
                          entry.is_paid 
                            ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                            : 'bg-red-500 text-white hover:bg-red-600'
                        }`}
                      >
                        {updating === entry.id ? '...' : entry.is_paid ? '✓ Paid' : 'Mark Paid'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
